import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { formatLongDate, minutesToClock, type ISODate } from '@app/shared'

import { DataModeBadge } from '../HeaderBand'
import { sourcePing } from '../../lib/motion'
import { modK } from '../../lib/platform'
import type { DayController, HandledRow, ToastState } from './controller'
import { REASON_CHIPS, quietTailLine, type ComingDay, type SourceChip } from './designed'
import {
  ArrowRight,
  BellGlyph,
  BrokenLink,
  CalendarGlyph,
  CapGlyph,
  CheckGlyph,
  ClockGlyph,
  MailGlyph,
  MoonGlyph,
  Refresh,
  XGlyph,
} from './icons'
import { DayCard, Dot, Kc, Pill, RollingCount, SecHead, Sk, SrcLine, Tip, usePresence } from './parts'

// ============================================================================
// THE DAY PAGE'S FRAME AND ITS QUIETER BLOCKS — the top bar, the band header with its freshness
// chips and "Handled today", Your day, Coming up, the banners (14f), the quiet and first-run
// heroes, the loading skeletons, the undo toast (14d) and the shortcut sheet.
// ============================================================================

// ── Top bar (Main, Quiet, Degraded boards) ──────────────────────────────────

export function TopBar({ today, week, attention }: { today: ISODate; week: string | null; attention: string | null }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  return (
    <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-3 border-b border-line-3 px-[30px] py-[14px]">
      <span className="text-[12.5px] font-semibold text-ink">{formatLongDate(today)}</span>
      {week && <Pill tone="plain" className="text-ink-2">{week}</Pill>}
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search')
        }}
        className="mo-field box-border flex h-8 w-[320px] max-w-full items-center gap-2 rounded-button border border-line-2 bg-surface px-[10px] text-microcopy"
      >
        <SearchGlyph />
        <input
          type="search"
          aria-label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search entries, classes, notes"
          className="min-w-0 flex-1 border-0 bg-transparent font-[inherit] text-[12px] text-ink outline-none placeholder:text-microcopy"
        />
        <Kc>{modK}</Kc>
      </form>
      <DataModeBadge attention={attention} />
    </div>
  )
}

function SearchGlyph() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-none">
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4-4" />
    </svg>
  )
}

// ── The band header ─────────────────────────────────────────────────────────

/** A source's freshness chip. Pings once and swaps its time when a sync lands (14e). */
export function FreshChip({ chip }: { chip: SourceChip }) {
  const dot = useRef<HTMLSpanElement>(null)
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    // A source that failed does not ping (14f).
    if (chip.state === 'fresh') sourcePing(dot.current)
  }, [chip.value, chip.state])
  const tone = chip.state === 'stale' ? 'text-warning' : chip.state === 'disconnected' ? 'text-danger' : 'text-ink-3'
  const dotTone =
    chip.state === 'stale' ? 'bg-warning-dot' : chip.state === 'disconnected' ? 'bg-danger-dot' : chip.state === 'loading' ? 'bg-faint' : 'bg-success-dot'
  return (
    <span className={clsx('day-fresh inline-flex items-center gap-[5px] whitespace-nowrap text-[11px]', chip.state === 'loading' ? 'text-microcopy' : tone)}>
      <span ref={dot} className={clsx('day-dot inline-block size-[6px] flex-none rounded-full', dotTone)} aria-hidden />
      {chip.state === 'loading' ? (
        `${chip.name} · loading`
      ) : (
        <span>
          {chip.name} <RollingCount value={chip.value} />
        </span>
      )}
    </span>
  )
}

export function DayBand({
  title,
  summary,
  right,
}: {
  title: ReactNode
  summary: ReactNode
  right?: ReactNode
}) {
  return (
    <header className="flex flex-none flex-wrap items-center gap-x-[18px] gap-y-[10px] px-[30px] pb-[15px] pt-[18px]" style={{ background: 'var(--band-day)' }}>
      <div className="flex flex-wrap items-baseline gap-3">
        {title}
        {summary}
      </div>
      {right && <div className="ml-auto flex flex-wrap items-center gap-x-[14px] gap-y-2">{right}</div>}
    </header>
  )
}

export function BandTitle({ children }: { children: ReactNode }) {
  return <h1 className="m-0 text-[15px] font-semibold leading-none tracking-[-0.01em] text-ink">{children}</h1>
}

/** "Handled today · 2" — and what was handled, with Undo, opened from the header. */
export function HandledButton({ count, rows, nowLabel }: { count: number; rows: HandledRow[]; nowLabel: (minutes: string) => string }) {
  const [open, setOpen] = useState(false)
  const { mounted, closing } = usePresence(open)
  const before = Math.max(0, count - rows.length)
  return (
    <span className="relative">
      <button type="button" className="day-btn day-tipw h-7" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <CheckGlyph size={14} className="text-success" />
        <span>
          Handled today · <RollingCount value={count} />
        </span>
        <Tip name="Handled today" align="right">
          Done and dismissed today. Open it to undo one.
        </Tip>
      </button>
      {mounted && (
        <div
          role="dialog"
          aria-label="Handled today"
          data-closing={closing || undefined}
          className="day-menu absolute right-0 top-[calc(100%+6px)] z-30 w-[380px] rounded-panel border border-line bg-surface shadow-frame"
        >
          <div className="px-[14px] pb-2 pt-3 text-[12.5px] font-semibold">
            Handled today <span className="font-medium text-muted">{count}</span>
          </div>
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-[10px] border-t border-line-3 px-4 py-[9px] text-[12px]">
              <span className={clsx('w-[70px] text-[11.5px] font-semibold', r.verb === 'Done' ? 'text-success' : 'text-muted')}>{r.verb}</span>
              <span className="min-w-0 flex-1 truncate text-ink-2">{r.title}</span>
              <span className="text-[11px] text-microcopy">{nowLabel(r.at)}</span>
              <button type="button" className="day-btn h-[26px]" onClick={() => r.undo()}>
                Undo
              </button>
            </div>
          ))}
          {before > 0 && (
            <div className="border-t border-line-3 px-4 py-[9px] text-[11px] leading-[1.45] text-microcopy">
              {before} more handled before this page was opened. Only what you handle here is listed, so only that can be undone here.
            </div>
          )}
        </div>
      )}
    </span>
  )
}

// ── Banners (14f): fade in place, never slide ───────────────────────────────

export function DisconnectedBanner({ name, address, since, others, onReconnect, busy }: { name: string; address: string; since: string | null; others: string | null; onReconnect: () => void; busy: boolean }) {
  return (
    <section className="day-fadein flex flex-wrap items-center gap-x-4 gap-y-[10px] rounded-card border border-danger-line bg-danger-fill px-4 py-[14px]" aria-label={`${name} inbox is disconnected`}>
      <BrokenLink size={16} className="text-danger-2" />
      <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-[3px]">
        <h2 className="m-0 text-[12.5px] font-semibold text-danger">{name} inbox is disconnected</h2>
        <p className="m-0 text-[12px] leading-[1.45] text-danger">
          {address} stopped allowing access, so its mail{since ? ` since ${since}` : ''} is not on this page.{others ? ` ${others}` : ''}
        </p>
      </div>
      <button type="button" disabled={busy} onClick={onReconnect} className="day-tipw h-8 cursor-pointer rounded-button border-0 bg-danger-solid px-[14px] font-[inherit] text-[12px] font-semibold text-on-dark disabled:opacity-60">
        {busy ? 'Opening Google…' : 'Reconnect'}
        <Tip name="Reconnect" align="right">
          Opens Google to allow read-only access again. Its mail since then is read on the next sync.
        </Tip>
      </button>
    </section>
  )
}

export function StaleBanner({ name, asOf, error }: { name: string; asOf: string; error: string | null }) {
  return (
    <section className="day-fadein flex gap-[10px] rounded-card border border-warning-line bg-warning-fill px-[14px] py-3" aria-label={`${name} mail is ${asOf}`}>
      <ClockGlyph size={16} className="mt-px text-warning-2" />
      <div className="flex flex-col gap-[3px]">
        <h2 className="m-0 text-[12.5px] font-semibold text-warning">
          {name} mail is {asOf}
        </h2>
        <p className="m-0 text-[11.5px] leading-[1.45] text-warning">
          {error ? `${error} There is nothing for you to do.` : 'The last sync has not come back yet.'} Its older mail is still sorted and shown.
        </p>
      </div>
    </section>
  )
}

// ── Your day ────────────────────────────────────────────────────────────────

export interface DayEvent {
  key: string
  start: number | null
  end: number | null
  title: string
  src: string[]
}

export function YourDay({
  events,
  nowMinutes,
  remindersToday,
  someday,
  showNow,
}: {
  events: DayEvent[]
  nowMinutes: number
  remindersToday: number
  someday: number
  showNow: boolean
}) {
  const timed = events.filter((e) => e.start !== null)
  const last = timed.reduce((m, e) => Math.max(m, e.end ?? e.start ?? 0), 0)
  const nowAt = timed.findIndex((e) => (e.start ?? 0) > nowMinutes)
  const rows: ReactNode[] = []
  const nowLine = (
    <div key="now" className="flex items-center gap-2 px-4 pb-2">
      <span className="w-14 text-[10.5px] font-semibold text-danger-2">{minutesToClock(nowMinutes)}</span>
      <Dot className="bg-danger-dot" />
      <span className="h-px flex-1 bg-now-line" />
    </div>
  )
  events.forEach((e, i) => {
    if (showNow && e.start !== null && timed.indexOf(e) === nowAt && nowAt >= 0) rows.push(nowLine)
    rows.push(
      <div key={e.key} className={clsx('flex gap-3 px-4 py-[11px]', (i > 0 || !(showNow && nowAt === 0)) && 'border-t border-line-3')}>
        <div className="w-14 flex-none text-[11.5px] font-medium leading-[1.35] text-ink-2">
          {e.start === null ? 'all day' : minutesToClock(e.start)}
          {e.end !== null && (
            <>
              <br />
              <span className="font-normal text-microcopy">{minutesToClock(e.end)}</span>
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-[12.5px] font-semibold leading-[1.3] text-ink">{e.title}</span>
          <SrcLine parts={e.src} />
        </div>
      </div>,
    )
  })
  return (
    <DayCard aria-labelledby="h-day">
      <SecHead
        id="h-day"
        icon={<CalendarGlyph className="text-school" />}
        title="Your day"
        count={events.length > 0 ? `${events.length} ${events.length === 1 ? 'event' : 'events'}` : undefined}
        note={timed.length > 0 ? `ends ${minutesToClock(last)}` : undefined}
      />
      {events.length === 0 ? (
        <div className="border-t border-line-3 px-4 pb-[14px] pt-3 text-[12px] text-ink-3">No classes or events today.</div>
      ) : (
        rows
      )}
      <div className="flex items-center gap-2 rounded-b-card border-t border-line-3 px-4 py-[11px] text-[11.5px] text-microcopy">
        <BellGlyph size={14} className="text-tasks" />
        <span>{remindersToday === 0 ? 'No reminders due today' : `${remindersToday} ${remindersToday === 1 ? 'reminder' : 'reminders'} due today`}</span>
        {someday > 0 && (
          <Link to="/tasks" className="ml-auto text-[11.5px] text-ink-2 hover:text-ink">
            {someday} in Someday
          </Link>
        )}
      </div>
    </DayCard>
  )
}

// ── Coming up ───────────────────────────────────────────────────────────────

export function ComingUp({ days, count, to, quietTail, toLabel }: { days: ComingDay[]; count: number; to: ISODate; quietTail: [ISODate, ISODate] | null; toLabel: string }) {
  void to
  return (
    <DayCard aria-labelledby="h-next">
      <SecHead id="h-next" icon={<ArrowRight className="text-muted" />} title="Coming up" count={count} note={`next 7 days · to ${toLabel}`} />
      {days.map((d) => (
        <div key={d.date}>
          <div className="border-t border-line-3 px-4 pb-1 pt-[9px] text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{dayEyebrow(d.date)}</div>
          {d.rows.map((r, i) => (
            <div key={r.key} className={clsx('flex items-baseline gap-[10px] px-4', i === d.rows.length - 1 ? 'pb-[10px] pt-[6px]' : 'py-[6px]')}>
              <span className="text-[12.5px] font-medium leading-[1.3] text-ink">{r.title}</span>
              <span className="text-[11px] text-microcopy">{r.course}</span>
              {r.time && <span className="ml-auto whitespace-nowrap text-[11.5px] font-medium text-microcopy">{r.time}</span>}
            </div>
          ))}
        </div>
      ))}
      {quietTail && (
        <div className="rounded-b-card border-t border-line-3 bg-surface-3 px-4 py-[10px] text-[11px] text-microcopy">{quietTailLine(quietTail)}</div>
      )}
    </DayCard>
  )
}

function dayEyebrow(date: ISODate): string {
  const d = new Date(`${date}T12:00:00Z`)
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()]} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]} ${d.getUTCDate()}`
}

// ── Heroes ──────────────────────────────────────────────────────────────────

export function Hero({ title, body, compact }: { title: string; body: string; compact?: boolean }) {
  return (
    <section className={clsx('flex flex-col gap-3 px-[6px]', compact ? 'pb-2 pt-[30px]' : 'pb-[30px] pt-10')}>
      <h2 className="m-0 text-[26px] font-light leading-[1.25] tracking-[-0.02em] text-ink">{title}</h2>
      <p className="m-0 max-w-[600px] text-[13.5px] leading-[1.65] text-ink-3 text-pretty">{body}</p>
    </section>
  )
}

export interface FirstRunSource {
  key: string
  title: string
  src: string
  kind: 'inbox' | 'bcourses' | 'calendar'
  connected: boolean
}

export function FirstRunSources({ sources, onConnect, busy, error }: { sources: FirstRunSource[]; onConnect: () => void; busy: boolean; error: string | null }) {
  const connected = sources.filter((s) => s.connected).length
  let primaryGiven = false
  return (
    <DayCard aria-labelledby="f-acc">
      <SecHead id="f-acc" title="Sources" count={`${connected} of ${sources.length} connected`} />
      {sources.map((s) => {
        const primary = !s.connected && s.kind === 'inbox' && !primaryGiven
        if (primary) primaryGiven = true
        return (
          <div key={s.key} className="flex items-center gap-3 border-t border-line-3 px-4 py-[13px]">
            <span
              className={clsx(
                'flex size-[30px] flex-none items-center justify-center rounded-tile border',
                s.connected ? 'border-solid border-line' : 'border-dashed border-dash text-muted',
                s.kind === 'bcourses' && 'text-school',
                s.kind === 'calendar' && 'text-danger-2',
              )}
            >
              {s.kind === 'bcourses' ? <CapGlyph size={15} /> : s.kind === 'calendar' ? <CalendarGlyph size={15} /> : <MailGlyph size={15} />}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
              <span className="text-[12.5px] font-semibold leading-[1.3] text-ink">{s.title}</span>
              <span className="text-[11px] leading-[1.4] text-microcopy">{s.src}</span>
            </div>
            {s.connected ? (
              <span className="inline-flex items-center gap-[6px] text-[11.5px] text-success">
                <Dot className="bg-success-dot" />
                Connected
              </span>
            ) : s.kind === 'inbox' ? (
              <button type="button" disabled={busy} onClick={onConnect} className={clsx('day-btn day-tipw px-3', primary && 'day-btn-solid')}>
                {busy ? 'Opening Google…' : 'Connect'}
                <Tip name={`Connect ${s.title}`} align="right">
                  Opens Google. Read-only: nothing is sent, archived, labelled or deleted.
                </Tip>
              </button>
            ) : (
              <Link to="/settings/connections" className="day-btn px-3">
                Connect
              </Link>
            )}
          </div>
        )
      })}
      {error && <div className="border-t border-line-3 px-4 py-[11px] text-[11px] leading-[1.5] text-danger">{error}</div>}
      <div className="rounded-b-card border-t border-line-3 bg-surface-3 px-4 py-[11px] text-[11px] leading-[1.5] text-microcopy">
        Kept for each email: sender, subject, Gmail's short snippet, Claude's sorting and a link to the thread. Never the body.
      </div>
    </DayCard>
  )
}

// ── Weekly check, on the Day page ───────────────────────────────────────────

export function WeeklyCard({ items, to }: { items: number; to: string }) {
  return (
    <DayCard className="flex flex-wrap items-center gap-x-[14px] gap-y-[10px] px-4 py-3">
      <Refresh className="text-chat" />
      <span className="text-[12.5px] font-semibold">Weekly check is ready</span>
      <span className="text-[11.5px] text-microcopy">{items} items from the folded piles · about 2 minutes</span>
      <Link to={to} className="day-btn day-tipw ml-auto" data-day-weekly="">
        Open
        <Tip name="Weekly check" k="W" align="right">
          Ten from the folded piles, and the rules Claude proposes. Nothing changes until you answer.
        </Tip>
      </Link>
    </DayCard>
  )
}

// ── Loading (board 7): each section swaps skeleton for content as its source answers ──

const NEEDS_SK = [
  { t: '52%', w: '78%' },
  { t: '44%', w: '62%' },
  { t: '58%', w: '70%' },
]

export function NeedsSkeleton() {
  return (
    <DayCard aria-label="Needs you today, loading">
      <SecHead icon={<SunriseIcon />} title="Needs you today" />
      {NEEDS_SK.map((r, i) => (
        <div key={i} className="flex gap-3 border-t border-line-3 px-4 pb-[13px] pt-3">
          <Sk w={28} h={28} round={9} />
          <div className="flex flex-1 flex-col gap-2 pt-[2px]">
            <div className="flex gap-[10px]">
              <Sk w={r.t} h={11} />
              <Sk w={52} h={11} className="ml-auto" />
            </div>
            <Sk w={r.w} h={10} second />
            <Sk w="38%" h={8} />
          </div>
        </div>
      ))}
    </DayCard>
  )
}

export function WaitingSkeleton() {
  return (
    <DayCard aria-label="Still waiting, loading">
      <SecHead icon={<ClockGlyph className="text-tasks" />} title="Still waiting" />
      {['36%', '60%'].map((t, i) => (
        <div key={i} className="flex gap-3 border-t border-line-3 px-4 pb-[13px] pt-3">
          <Sk w={28} h={28} round={9} />
          <div className="flex flex-1 flex-col gap-2 pt-[2px]">
            <div className="flex gap-[10px]">
              <Sk w={t} h={11} />
              <Sk w={54} h={14} round={20} className="ml-auto" />
            </div>
            <Sk w="24%" h={8} />
          </div>
        </div>
      ))}
    </DayCard>
  )
}

export function DaySkeleton() {
  return (
    <DayCard aria-label="Your day, loading">
      <SecHead icon={<CalendarGlyph className="text-school" />} title="Your day" />
      {['70%', '46%'].map((t, i) => (
        <div key={i} className="flex gap-3 border-t border-line-3 px-4 py-3">
          <div className="flex w-14 flex-col gap-[6px]">
            <Sk w={48} h={10} />
            <Sk w={40} h={8} />
          </div>
          <div className="flex flex-1 flex-col gap-[7px]">
            <Sk w={t} h={11} />
            <Sk w="40%" h={8} />
          </div>
        </div>
      ))}
    </DayCard>
  )
}

export function ComingSkeleton() {
  return (
    <DayCard aria-label="Coming up, loading">
      <SecHead icon={<ArrowRight className="text-muted" />} title="Coming up" />
      {['42%', '58%', '34%'].map((t, i) => (
        <div key={i} className="flex gap-[10px] border-t border-line-3 px-4 py-[11px]">
          <Sk w={t} h={10} />
          <Sk w={46} h={10} className="ml-auto" />
        </div>
      ))}
    </DayCard>
  )
}

function SunriseIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-none text-urgent-time">
      <path d="M3 18h18" />
      <path d="M7 18a5 5 0 0 1 10 0" />
      <path d="M12 6v4M5.5 10.5l1.6 1.6M18.5 10.5l-1.6 1.6" />
    </svg>
  )
}

// ── The undo toast (14d) ────────────────────────────────────────────────────

export function UndoToast({ toast, ctrl }: { toast: ToastState | null; ctrl: DayController }) {
  if (!toast) return null
  const icon =
    toast.icon === 'done' ? (
      <CheckGlyph size={14} className="text-success-dot" />
    ) : toast.icon === 'snooze' ? (
      <MoonGlyph size={14} className="text-toast-micro" />
    ) : toast.icon === 'move' ? (
      <ArrowRight size={14} className="text-toast-micro" />
    ) : toast.icon === 'error' ? (
      <XGlyph size={14} className="text-danger-dot" />
    ) : (
      <XGlyph size={14} className="text-toast-micro" />
    )
  return (
    <div
      role="status"
      data-on={toast.on ? 'true' : 'false'}
      className="day-toast flex max-w-[calc(100vw-120px)] items-center gap-[14px] whitespace-nowrap rounded-panel bg-ink py-[10px] pl-4 pr-[10px] text-[12px] text-page shadow-day-toast"
    >
      {icon}
      <span className="min-w-0 truncate">
        <b className="font-semibold">{toast.verb}</b> · {toast.title}
      </span>
      {toast.chips && !toast.noted && (
        <span className="flex items-center gap-[5px]">
          <span className="text-[11.5px] text-toast-micro">Why?</span>
          {REASON_CHIPS.map((c) => (
            <button key={c.reason} type="button" className="day-chip" onClick={() => ctrl.pickReason(c.reason)}>
              {c.label}
            </button>
          ))}
        </span>
      )}
      {toast.noted && <span className="text-[11.5px] text-toast-micro">noted · {toast.noted}</span>}
      {toast.undo && (
        <button
          type="button"
          onClick={() => ctrl.undo()}
          title="Undo (Z) — puts it back where it was"
          className="inline-flex h-7 cursor-pointer items-center gap-[6px] rounded-button-sm border border-toast-micro bg-transparent px-[10px] font-[inherit] text-[12px] font-semibold text-page"
        >
          Undo
          <span className="font-mono text-[10px] text-toast-micro">Z</span>
        </button>
      )}
      <span className="day-drain" aria-hidden />
    </div>
  )
}

// ── The shortcut sheet (Keyboard board) ─────────────────────────────────────

export function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { mounted, closing } = usePresence(open)
  if (!mounted) return null
  const row = (keys: ReactNode, text: ReactNode) => (
    <div className="flex items-center gap-[10px] py-[7px] text-[12px] text-ink-2">
      <span className="flex w-[86px] flex-none gap-1">{keys}</span>
      {text}
    </div>
  )
  const grp = (t: string) => <div className="pb-[2px] pt-[10px] text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{t}</div>
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Day page shortcuts"
        data-closing={closing || undefined}
        className="day-menu w-[400px] max-w-[calc(100vw-32px)] rounded-panel border border-line bg-surface px-[18px] pb-4 pt-[14px] shadow-frame"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center">
          <span className="text-[13.5px] font-semibold">Day page shortcuts</span>
          <button type="button" onClick={onClose} className="ml-auto cursor-pointer border-0 bg-transparent p-0" aria-label="Close">
            <Kc large>Esc</Kc>
          </button>
        </div>
        {grp('Move around')}
        {row(<><Kc large>J</Kc><Kc large>K</Kc></>, 'Next and previous card, across every section')}
        {row(<Kc large>Enter</Kc>, 'Open or close a folded pile')}
        {row(<Kc large>W</Kc>, 'Weekly check, when it is ready')}
        {grp('On the focused card')}
        {row(<Kc large>D</Kc>, 'Done')}
        {row(<Kc large>S</Kc>, <span>Snooze, then <Kc large>1</Kc> <Kc large>2</Kc> <Kc large>3</Kc> or <Kc large>C</Kc> for a date</span>)}
        {row(<Kc large>X</Kc>, 'Dismiss')}
        {row(<Kc large>O</Kc>, 'Open in Gmail or bCourses')}
        {row(<><Kc large>1</Kc><Kc large>–</Kc><Kc large>4</Kc></>, 'Move to Today, Important, Low priority, Noise')}
        {row(<Kc large>R</Kc>, 'Sender rules')}
        {row(<Kc large>N</Kc>, 'Next steps Claude proposes for this mail')}
        {grp('Anywhere')}
        {row(<Kc large>Z</Kc>, 'Undo, while the toast is showing')}
        {row(<Kc large>?</Kc>, 'This list')}
      </div>
    </div>
  )
}

/** '7:57 AM' from the controller's stored minutes. */
export function handledAt(minutes: string): string {
  const n = Number(minutes)
  return Number.isFinite(n) ? minutesToClock(n) : ''
}

